/**
 * OPFS (Origin Private File System) caching layer for GGUF model files.
 *
 * Stores models under `root/models/<name>` so they survive page reloads
 * without requiring the user to re-select files each time.
 */

const MODELS_DIR = "models";

async function getModelsDir(): Promise<FileSystemDirectoryHandle> {
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(MODELS_DIR, { create: true });
}

export async function hasCachedModel(name: string): Promise<boolean> {
  try {
    const dir = await getModelsDir();
    await dir.getFileHandle(name);
    return true;
  } catch {
    return false;
  }
}

export async function getCachedModel(name: string): Promise<File | null> {
  try {
    const dir = await getModelsDir();
    const handle = await dir.getFileHandle(name);
    return handle.getFile();
  } catch {
    return null;
  }
}

export async function saveCachedModel(
  name: string,
  blob: Blob,
): Promise<void> {
  const dir = await getModelsDir();
  const handle = await dir.getFileHandle(name, { create: true });
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
}

export async function deleteCachedModel(name: string): Promise<void> {
  try {
    const dir = await getModelsDir();
    await dir.removeEntry(name);
  } catch {
    // already gone — nothing to do
  }
}

export async function getCacheStatus(): Promise<{
  backbone: boolean;
  mmproj: boolean;
}> {
  const [backbone, mmproj] = await Promise.all([
    hasCachedModel("backbone.gguf"),
    hasCachedModel("mmproj.gguf"),
  ]);
  return { backbone, mmproj };
}
